import React, { useState, useMemo, useEffect } from "react";
import { Search, MapPin, Clock, Wallet, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../data/mock";
import { getJobs, getCategories } from "../api";

const Jobs = ({ onApply }) => {
  const { t, lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(-1);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Localized value with fallback (es -> en -> pl)
  const loc = (obj) => obj?.[lang] || obj?.en || obj?.pl || "";

  // Lookup: category index -> localized name
  const catName = (index) => {
    const c = categories.find((cat) => cat.index === index);
    return c ? loc(c.name) : "";
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([getJobs(), getCategories()])
      .then(([jobsData, catsData]) => {
        if (!mounted) return;
        setJobs(Array.isArray(jobsData) ? jobsData : []);
        setCategories(Array.isArray(catsData) ? catsData : []);
      })
      .catch(() => {
        if (mounted) {
          setJobs([]);
          setCategories([]);
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((job) => {
      const matchCat = activeCat === -1 || job.category_index === activeCat;
      const t = (job.title?.[lang] || job.title?.en || job.title?.pl || "").toLowerCase();
      const l = (job.location?.[lang] || job.location?.en || job.location?.pl || "").toLowerCase();
      const matchQ = !q || t.includes(q) || l.includes(q);
      return matchCat && matchQ;
    });
  }, [query, activeCat, lang, jobs]);

  const benefits = [t.jobs.benefit1, t.jobs.benefit2, t.jobs.benefit3, t.jobs.benefit4];

  return (
    <div className="reveal">
      <div className="grid gap-10 lg:grid-cols-3">
        {/* Benefits sidebar */}
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-2xl">
            <img src={IMAGES.workers} alt="Workers" className="grayscale-img h-56 w-full object-cover" />
          </div>
          <div className="glass-card mt-5 rounded-2xl p-6">
            <h4 className="font-display text-lg font-semibold uppercase tracking-wide text-white">
              {t.jobs.benefitsTitle}
            </h4>
            <ul className="mt-4 space-y-3">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/65">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Job list */}
        <div className="lg:col-span-2">
          {/* Search */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.jobs.searchPlaceholder}
              className="w-full rounded-full border border-white/15 bg-white/5 py-3.5 pl-12 pr-5 text-sm text-white placeholder:text-white/40 outline-none backdrop-blur-md transition-colors focus:border-white/40"
            />
          </div>

          {/* Category chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCat(-1)}
              className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                activeCat === -1
                  ? "border-white bg-white text-black"
                  : "border-white/15 text-white/65 hover:border-white/40 hover:text-white"
              }`}
            >
              {t.jobs.allCategories}
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.index)}
                className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                  activeCat === c.index
                    ? "border-white bg-white text-black"
                    : "border-white/15 text-white/65 hover:border-white/40 hover:text-white"
                }`}
              >
                {loc(c.name)}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="mt-6 space-y-4">
            {loading && (
              <div className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-6 py-10 text-sm text-white/50">
                <Loader2 className="h-4 w-4 animate-spin" /> ...
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <p className="rounded-xl border border-white/10 bg-white/5 px-6 py-10 text-center text-sm text-white/50">
                {t.jobs.noResults}
              </p>
            )}
            {!loading && filtered.map((job) => (
              <div
                key={job.id}
                className="glass-card group flex flex-col gap-4 rounded-2xl p-6 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <span className="inline-block rounded-full border border-white/15 px-3 py-0.5 text-[0.65rem] uppercase tracking-wider text-white/60">
                    {catName(job.category_index)}
                  </span>
                  <h4 className="mt-2 font-display text-xl font-semibold text-white">{loc(job.title)}</h4>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-white/55">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {loc(job.location)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {loc(job.type)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5" /> {job.salary}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onApply?.(loc(job.title))}
                  className="glass-btn-solid glass-btn shrink-0 px-5 py-2.5 text-sm font-semibold"
                >
                  {t.jobs.apply}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Jobs;
